import type {
  AINewsDigest,
  CatalogItem,
  GraphListItem,
  GraphListResponse,
  InitiativeItem,
  LibraryItem,
  ListKind,
  PlatformItem,
  TrainingFormat,
  TrainingSession,
} from "../types/navigator";
import { formatPriority } from "../utils/priority";
import { logActivity } from "./activityLog";
import type { ActivityCategory } from "./activityLog";

export const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

export const SITE_ID = import.meta.env.VITE_SP_SITE_ID;
export const LIBRARY_LIST_ID = import.meta.env.VITE_SP_LIST_ID;
export const INITIATIVE_LIST_ID =
  import.meta.env.VITE_SP_INITIATIVE_LIST_ID ??
  "abb9df8a-b115-4998-9796-8c20f98358f3";
export const PLATFORM_LIST_ID =
  import.meta.env.VITE_SP_PLATFORM_LIST_ID ??
  "6949097b-48a2-43fd-bbf2-f3e983f429c3";
export const NEWS_LIST_ID =
  import.meta.env.VITE_SP_NEWS_LIST_ID ??
  "85201e1f-3ae7-4a17-a9ff-6004977c986d";
export const TRAINING_LIST_ID =
  import.meta.env.VITE_SP_TRAINING_LIST_ID ??
  "b0ee78c6-633d-4076-a2ff-2c173d92ba7b";

if (!SITE_ID || !LIBRARY_LIST_ID) {
  throw new Error("VITE_SP_SITE_ID and VITE_SP_LIST_ID must be set.");
}

// ---------- Field helpers ----------

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object" && v && "Description" in v && "Url" in v) {
    return String((v as { Url?: unknown }).Url ?? "");
  }
  return "";
}

function pickUrl(v: unknown): { url: string; label: string } {
  if (!v) return { url: "", label: "" };
  if (typeof v === "string") return { url: v, label: "" };
  if (typeof v === "object") {
    const obj = v as { Url?: unknown; Description?: unknown };
    return { url: str(obj.Url), label: str(obj.Description) };
  }
  return { url: "", label: "" };
}

// ---------- Mappers (exported for per-page use) ----------

let _likeFieldLogged = false;
export function mapLibrary(raw: GraphListItem): LibraryItem {
  const f = raw.fields ?? {};
  if (!_likeFieldLogged) {
    _likeFieldLogged = true;
    const likeKeys = Object.keys(f).filter(k => /like/i.test(k));
    console.log("[mapLibrary] All field keys:", Object.keys(f).join(", "));
    console.log("[mapLibrary] Like-related keys:", likeKeys.length ? likeKeys.join(", ") : "(none found)");
  }
  const { url, label } = pickUrl((f as Record<string, unknown>).URL);
  return {
    id: raw.id,
    kind: "library",
    title:
      str(f.Title) ||
      str(f.LinkTitle) ||
      str((f as Record<string, unknown>).AssetName) ||
      "Untitled",
    description: str(f.Description),
    assetType: str((f as Record<string, unknown>).AssetType),
    aiPlatform: str((f as Record<string, unknown>).AIPlatform),
    complexity: str((f as Record<string, unknown>).Complexity),
    benefitType: str((f as Record<string, unknown>).BenefitType),
    instructions: str((f as Record<string, unknown>).Instructions),
    department: str((f as Record<string, unknown>).Department),
    license: str((f as Record<string, unknown>).LicenseRequired),
    likes: (() => { const n = Number(str((f as Record<string, unknown>).LikesCount)); return isNaN(n) ? 0 : n; })(),
    status: str((f as Record<string, unknown>).AssetStatus),
    url,
    urlLabel: label,
    webUrl: raw.webUrl,
    modified: raw.lastModifiedDateTime ?? str(f.Modified),
    created: raw.createdDateTime ?? str(f.Created),
    raw: {
      ...(f as Record<string, unknown>),
      _createdByName: raw.createdBy?.user?.displayName ?? "",
      _createdByEmail: (raw.createdBy?.user?.email ?? "").toLowerCase(),
    },
  };
}

export function mapInitiative(raw: GraphListItem): InitiativeItem {
  const f = (raw.fields ?? {}) as Record<string, unknown>;
  return {
    id: raw.id,
    kind: "initiative",
    title: str(f.Title) || str(f.LinkTitle) || "Untitled initiative",
    description: str(f.Whatproblemareyoutryingtosolve),
    submittedBy: str(f.Yourname),
    team: str(f.Yourteam_x002f_function),
    region: str(f.Region_x002f_Division),
    problem: str(f.Whatproblemareyoutryingtosolve),
    problemFrequency: str(f.Howoftendoesthisproblemoccur_x00),
    impactedToday: str(f.Whoisimpactedbythisproblemtoday_),
    ifSolved: str(f.Ifthisproblemweresolved_x002c_wh),
    anticipatedImpact: str(f.Pleaseexplaintheanticipatedimpac),
    // Multi-choice column — Graph returns an array. Join for display.
    impactType: pickArray(f.Whattypeofimpacttodoyouexpect_x0).join(", "),
    impactTypes: pickArray(f.Whattypeofimpacttodoyouexpect_x0),
    status: str(f.Status),
    priority: formatPriority(str(f.Priority)),
    rating: str(f.Rating),
    businessValueScore: str(f["Business Value Score"] ?? f.BusinessValueScore),
    technicalFeasibilityScore: str(
      f["Technical Feasibility Score"] ?? f.TechnicalFeasibilityScore
    ),
    developmentTeam: str(f["Development Team"] ?? f.DevelopmentTeam),
    // Real SharePoint internals (display-name keys never existed in the field
    // bag, so these four previously always read blank). Fallbacks kept for
    // safety in case the column is ever renamed.
    proposedSolution: str(
      f.Ifyes_x002c_brieflydescribethepr ??
        f["Proposed solution"] ??
        f.ProposedSolution
    ),
    existingExamples: str(
      f.Arethereexistingexampleswecouldm ??
        f["Existing examples"] ??
        f.ExistingExamples
    ),
    dataInputs: str(
      f.Whatdataorinputswouldbeinvolved_ ??
        f["Data/inputs"] ??
        f.DataInputs
    ),
    constraintsRisks: str(
      f.Arethereanyconstraints_x002c_ris ??
        f["Constraints/risks"] ??
        f.ConstraintsRisks
    ),
    anythingElse: str(f.Anythingelseweshouldknow_x003f_),
    notes: str(f.Notes),
    webUrl: raw.webUrl,
    modified: raw.lastModifiedDateTime,
    created: raw.createdDateTime,
    raw: {
      ...f,
      _createdByName: raw.createdBy?.user?.displayName ?? "",
      _createdByEmail: (raw.createdBy?.user?.email ?? "").toLowerCase(),
    },
  };
}

export function mapPlatform(raw: GraphListItem): PlatformItem {
  const f = (raw.fields ?? {}) as Record<string, unknown>;
  return {
    id: raw.id,
    kind: "platform",
    title: str(f.Title) || str(f.LinkTitle) || "Untitled platform",
    description: str(f.Purpose) || str(f.Notes),
    purpose: str(f.Purpose),
    keyCapabilities: str(f.Notes),
    status: str(f.Status),
    platformType: str(f.PlatformType),
    licenseType: str(f.LicenseType),
    complexity: str(f.Complexity),
    usedByBroaderBrookfield: str(f.UsedbybroaderBrookfield),
    // Approved Users + Cost Model are multi-choice columns — Graph returns an
    // array. Join for display; str() alone would read them blank.
    approvedUse: pickArray(f.ApprovedUse).join(", ") || str(f.ApprovedUse),
    costModel: pickArray(f.CostModel).join(", ") || str(f.CostModel),
    webUrl: raw.webUrl,
    modified: raw.lastModifiedDateTime,
    created: raw.createdDateTime,
    raw: f,
  };
}

// ---------- Paged fetch ----------

export interface ListPageParams {
  token: string;
  listId: string;
  pageSize?: number;
  /** Multiple values per field are combined with `or`; fields combine with
   * `and`. A `string` value is treated as a single-element array. */
  filters?: Record<string, string | string[]>;
  nextLink?: string;
}

export interface ListPageResult {
  items: GraphListItem[];
  nextLink?: string;
}

// SharePoint $search on list items via Graph is unreliable (only hits indexed
// columns, often returns nothing even when matches exist). We deliberately
// drop $search here and apply text search client-side over the mapped items —
// it covers all fields uniformly and is instant once a page is loaded.
export async function fetchListPage(
  params: ListPageParams
): Promise<ListPageResult> {
  let url: string;
  if (params.nextLink) {
    url = params.nextLink;
  } else {
    const u = new URL(
      `${GRAPH_BASE}/sites/${SITE_ID}/lists/${params.listId}/items`
    );
    u.searchParams.set("expand", "fields,createdBy");
    u.searchParams.set("$top", String(params.pageSize ?? 50));
    if (params.filters) {
      const esc = (s: string) => s.replace(/'/g, "''");
      const clauses = Object.entries(params.filters)
        .map(([k, raw]) => {
          const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
          if (values.length === 0) return null;
          const parts = values.map(
            (v) => `fields/${k} eq '${esc(v)}'`
          );
          return parts.length === 1 ? parts[0] : `(${parts.join(" or ")})`;
        })
        .filter((c): c is string => c !== null);
      if (clauses.length) {
        u.searchParams.set("$filter", clauses.join(" and "));
      }
    }
    url = u.toString();
  }

  const res = await fetch(url, {
    cache: "no-store",
    headers: {
      Authorization: `Bearer ${params.token}`,
      Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Graph API ${res.status}: ${res.statusText}${
        text ? ` — ${text.slice(0, 240)}` : ""
      }`
    );
  }
  const data = (await res.json()) as GraphListResponse;
  return { items: data.value, nextLink: data["@odata.nextLink"] };
}

// ---------- List column metadata (live choices + descriptions) ----------
//
// Lets forms pull their dropdown options and field hints straight from the
// SharePoint column definitions (List settings > column > Choices/Description)
// instead of a hardcoded copy that can drift out of sync. Falls back to a
// caller-supplied default when a column has no description, isn't a Choice
// column, or the fetch fails (offline, missing permission, etc.).

export interface SPColumnMeta {
  /** Internal (non-encoded) column name — matches the `fields` keys used elsewhere. */
  name: string;
  displayName: string;
  description: string;
  /** Choice values as currently configured in SharePoint, or null for non-choice columns. */
  choices: string[] | null;
}

interface GraphColumnDef {
  name: string;
  displayName?: string;
  description?: string;
  hidden?: boolean;
  readOnly?: boolean;
  choice?: { choices?: string[] };
}

interface GraphColumnResponse {
  value: GraphColumnDef[];
  "@odata.nextLink"?: string;
}

async function fetchListColumnsRaw(
  token: string,
  listId: string
): Promise<Record<string, SPColumnMeta>> {
  const out: Record<string, SPColumnMeta> = {};
  let url: string | undefined = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/columns`;
  while (url) {
    const res: Response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Graph columns ${res.status}: ${res.statusText}${
          text ? ` — ${text.slice(0, 240)}` : ""
        }`
      );
    }
    const data = (await res.json()) as GraphColumnResponse;
    for (const col of data.value) {
      if (col.hidden || col.readOnly) continue;
      out[col.name] = {
        name: col.name,
        displayName: col.displayName ?? col.name,
        description: (col.description ?? "").trim(),
        choices: col.choice?.choices ?? null,
      };
    }
    url = data["@odata.nextLink"];
  }
  return out;
}

// One in-flight/completed fetch per list — column schemas change rarely, so
// every form on the page shares a single request instead of each modal
// re-fetching the same ~30 columns.
const _listColumnsCache = new Map<string, Promise<Record<string, SPColumnMeta>>>();

export function getListColumns(
  token: string,
  listId: string
): Promise<Record<string, SPColumnMeta>> {
  let pending = _listColumnsCache.get(listId);
  if (!pending) {
    pending = fetchListColumnsRaw(token, listId).catch((err) => {
      _listColumnsCache.delete(listId);
      throw err;
    });
    _listColumnsCache.set(listId, pending);
  }
  return pending;
}

// ---------- Create item ----------
//
// Generic POST helper for any list. The caller passes the list ID and a
// flat `fields` map matching SharePoint's internal column names. Hyperlink
// columns (e.g. URL on the Library list) MUST be serialized as
// `{ Url, Description }` objects — never bare strings — or Graph rejects
// the request with 400.

export interface CreatedListItem {
  id: string;
  webUrl?: string;
  fields?: Record<string, unknown>;
}

// Multi-value columns (multi-choice "checkboxes", multi-lookup, multi-person)
// must be written to Graph as a JSON array AND carry an OData type annotation —
// `"<Field>@odata.type": "Collection(Edm.String)"`. Without the annotation Graph
// rejects the array with 400 (Initiative's "What type of impact" column); when a
// multi-value column is instead handed a bare string Graph 500s on the server
// (Platform's "Approved Users" / "Billing / Cost Model" columns). Auto-annotate
// every array-valued field so callers can just pass `string[]`.
function withMultiChoiceAnnotations(
  fields: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    out[key] = value;
    if (Array.isArray(value)) {
      out[`${key}@odata.type`] = "Collection(Edm.String)";
    }
  }
  return out;
}

export async function createListItem(
  token: string,
  listId: string,
  fields: Record<string, unknown>
): Promise<CreatedListItem> {
  const url = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items`;
  const body = { fields: withMultiChoiceAnnotations(fields) };
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Prefer: "apiversion=2.1",
  };
  console.log("[createListItem] POST", url, "\nheaders:", { ...headers, Authorization: "[redacted]" }, "\nbody:", JSON.stringify(body));
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error("[createListItem] error", res.status, res.statusText, "\nresponse body:", text);
    throw new Error(
      `Graph create ${res.status}: ${res.statusText}${
        text ? ` — ${text.slice(0, 300)}` : ""
      }`
    );
  }
  const data = (await res.json()) as GraphListItem;
  return { id: data.id, webUrl: data.webUrl, fields: data.fields };
}

// ---------- Update item ----------
//
// PATCH the item's fields bag. SharePoint accepts partial updates — only
// the keys you send are written. Hyperlink columns follow the same
// { Url, Description } shape as on create.

export async function updateListItem(
  token: string,
  listId: string,
  itemId: string,
  fields: Record<string, unknown>
): Promise<void> {
  // Use the parent item endpoint (same pattern as createListItem) rather than
  // the /fields sub-resource — some SP configurations reject the sub-resource
  // PATCH with 400 even for valid field values.
  const url = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items/${itemId}`;
  const body = { fields: withMultiChoiceAnnotations(fields) };
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Prefer: "apiversion=2.1",
  };
  console.log("[updateListItem] PATCH", url, "\nheaders:", { ...headers, Authorization: "[redacted]" }, "\nbody:", JSON.stringify(body));
  const res = await fetch(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error("[updateListItem] error", res.status, res.statusText, "\nresponse body:", text);
    throw new Error(
      `Graph update ${res.status}: ${res.statusText}${
        text ? ` — ${text.slice(0, 300)}` : ""
      }`
    );
  }
}

// ---------- Delete item ----------

export async function deleteListItem(
  token: string,
  listId: string,
  itemId: string
): Promise<void> {
  const url = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items/${itemId}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Graph delete ${res.status}: ${res.statusText}${
        text ? ` — ${text.slice(0, 300)}` : ""
      }`
    );
  }
}

/** Library-specific shape for the Add new form. Fields map 1:1 to the
 *  SharePoint Library list's internal column names. */
export interface NewLibraryFields {
  Title: string;
  Description?: string;
  AssetType?: string;
  AIPlatform?: string;
  Complexity?: string;
  BenefitType?: string;
  Instructions?: string;
  Department?: string;
  LicenseRequired?: string;
  AssetStatus?: string;
  /** Hyperlink column — sent to Graph as { Url, Description }. */
  URL?: { url: string; label?: string };
}

// SharePoint Hyperlink columns store Url + Description as one combined
// value with a ~255-character cap. Leave a little headroom for the internal
// delimiter SharePoint adds between the two parts.
export const MAX_HYPERLINK_LENGTH = 250;

export function hyperlinkTooLong(url: string, label?: string): boolean {
  return url.length + (label?.trim() || "Open").length > MAX_HYPERLINK_LENGTH;
}

export async function createLibraryItem(
  token: string,
  input: NewLibraryFields
): Promise<CreatedListItem> {
  const fields: Record<string, unknown> = {};
  // Only send fields that have a value — SharePoint rejects empty hyperlink
  // objects, and undefined/empty strings clutter the row's audit history.
  if (input.Title) fields.Title = input.Title;
  if (input.Description) fields.Description = input.Description;
  if (input.AssetType) fields.AssetType = input.AssetType;
  if (input.AIPlatform) fields.AIPlatform = input.AIPlatform;
  if (input.Complexity) fields.Complexity = input.Complexity;
  if (input.BenefitType) fields.BenefitType = input.BenefitType;
  if (input.Instructions) fields.Instructions = input.Instructions;
  if (input.Department) fields.Department = input.Department;
  if (input.LicenseRequired) fields.LicenseRequired = input.LicenseRequired;
  // Default to "Under Review" so new submissions land in a review queue
  // rather than auto-publishing to the catalog. ("Pending" is not a valid
  // choice on the Library list's AssetStatus column.)
  fields.AssetStatus = input.AssetStatus || "Under Review";
  // The Hyperlink column can't always be set atomically in the same POST
  // that creates the item — SharePoint 400s with a bare "Invalid request"
  // for some complex field types (Hyperlink, Lookup, Person/Group) when set
  // at creation time. Create first, then PATCH the link in as a follow-up.
  const created = await createListItem(token, LIBRARY_LIST_ID, fields);
  if (input.URL && input.URL.url) {
    await updateListItem(token, LIBRARY_LIST_ID, created.id, {
      // Default to "Open" rather than repeating the URL — Hyperlink columns
      // store Url + Description as one combined value with a ~255-char cap,
      // so duplicating a long URL into Description doubled its odds of
      // tripping that limit.
      URL: {
        Url: input.URL.url,
        Description: input.URL.label || "Open",
      },
    });
  }
  void logActivity("Library", "Created", `Submitted "${input.Title}"`, "library", created.id);
  return created;
}

export async function updateLibraryItem(
  token: string,
  itemId: string,
  input: Partial<NewLibraryFields>
): Promise<void> {
  const fields: Record<string, unknown> = {};
  if (input.Title) fields.Title = input.Title;
  if (input.Description !== undefined) fields.Description = input.Description;
  if (input.AssetType !== undefined) fields.AssetType = input.AssetType;
  if (input.AIPlatform !== undefined) fields.AIPlatform = input.AIPlatform;
  if (input.Complexity !== undefined) fields.Complexity = input.Complexity;
  if (input.BenefitType !== undefined) fields.BenefitType = input.BenefitType;
  if (input.Instructions !== undefined) fields.Instructions = input.Instructions;
  if (input.Department !== undefined) fields.Department = input.Department;
  if (input.LicenseRequired !== undefined) fields.LicenseRequired = input.LicenseRequired;
  if (input.AssetStatus) fields.AssetStatus = input.AssetStatus;
  if (input.URL?.url) {
    fields.URL = {
      Url: input.URL.url,
      Description: input.URL.label || "Open",
    };
  }
  await updateListItem(token, LIBRARY_LIST_ID, itemId, fields);
  void logActivity("Library", "Updated", `Updated "${input.Title ?? itemId}"`, "library", itemId);
}

export async function updateLibraryLikes(
  token: string,
  itemId: string,
  likes: number
): Promise<void> {
  await updateListItem(token, LIBRARY_LIST_ID, itemId, { LikesCount: likes });
}

/** Platform-specific shape for the Add new form. Internal SP column names. */
export interface NewPlatformFields {
  Title: string;
  Purpose?: string;
  Notes?: string;
  PlatformType?: string;
  LicenseType?: string;
  Complexity?: string;
  UsedbybroaderBrookfield?: string;
  // Approved Users + Billing/Cost Model are multi-choice ("checkBoxes") columns
  // — they must be written as arrays of selected labels, never bare strings.
  ApprovedUse?: string[];
  CostModel?: string[];
  Status?: string;
}

export async function createPlatformItem(
  token: string,
  input: NewPlatformFields
): Promise<CreatedListItem> {
  const fields: Record<string, unknown> = {};
  if (input.Title) fields.Title = input.Title;
  if (input.Purpose) fields.Purpose = input.Purpose;
  if (input.Notes) fields.Notes = input.Notes;
  if (input.PlatformType) fields.PlatformType = input.PlatformType;
  if (input.LicenseType) fields.LicenseType = input.LicenseType;
  if (input.Complexity) fields.Complexity = input.Complexity;
  if (input.UsedbybroaderBrookfield)
    fields.UsedbybroaderBrookfield = input.UsedbybroaderBrookfield;
  if (input.ApprovedUse && input.ApprovedUse.length)
    fields.ApprovedUse = input.ApprovedUse;
  if (input.CostModel && input.CostModel.length)
    fields.CostModel = input.CostModel;
  // "New" is the Platform list's real default status — "Pending" is not a valid
  // choice on this list's Status column (valid: New, Under IT Review, …).
  fields.Status = input.Status || "New";
  const created = await createListItem(token, PLATFORM_LIST_ID, fields);
  void logActivity("Platform", "Created", `Submitted "${input.Title}"`, "platform", created.id);
  return created;
}

export async function updatePlatformItem(
  token: string,
  itemId: string,
  input: Partial<NewPlatformFields>
): Promise<void> {
  const fields: Record<string, unknown> = {};
  if (input.Title) fields.Title = input.Title;
  if (input.Purpose) fields.Purpose = input.Purpose;
  if (input.Notes) fields.Notes = input.Notes;
  if (input.PlatformType) fields.PlatformType = input.PlatformType;
  if (input.LicenseType) fields.LicenseType = input.LicenseType;
  if (input.Complexity) fields.Complexity = input.Complexity;
  if (input.UsedbybroaderBrookfield)
    fields.UsedbybroaderBrookfield = input.UsedbybroaderBrookfield;
  if (input.ApprovedUse && input.ApprovedUse.length)
    fields.ApprovedUse = input.ApprovedUse;
  if (input.CostModel && input.CostModel.length)
    fields.CostModel = input.CostModel;
  if (input.Status) fields.Status = input.Status;
  await updateListItem(token, PLATFORM_LIST_ID, itemId, fields);
  void logActivity("Platform", "Updated", `Updated "${input.Title ?? itemId}"`, "platform", itemId);
}

/** Initiative-specific shape. Keys mirror SP's encoded internal column names
 *  (spaces -> _x0020_, slashes -> _x002f_, etc., often truncated to 32 chars). */
export interface NewInitiativeFields {
  Title: string;
  Yourname?: string;
  Yourteam_x002f_function?: string;
  Region_x002f_Division?: string;
  Whatproblemareyoutryingtosolve?: string;
  Howoftendoesthisproblemoccur_x00?: string;
  Whoisimpactedbythisproblemtoday_?: string;
  Ifthisproblemweresolved_x002c_wh?: string;
  Pleaseexplaintheanticipatedimpac?: string;
  // Multi-select choice column — write as an array of selected labels.
  Whattypeofimpacttodoyouexpect_x0?: string[];
  // Number column (1-5 reviewer score) — not a Low/Med/High text field.
  Priority?: number;
  ProposedSolution?: string;
  ExistingExamples?: string;
  DataInputs?: string;
  ConstraintsRisks?: string;
  AnythingElse?: string;
  Sponsor?: string;
  Status?: string;
  /** Admin-only field — not part of the submission form, shown/edited only
   *  in the admin Edit modal. */
  Notes?: string;
}

export async function createInitiativeItem(
  token: string,
  input: NewInitiativeFields
): Promise<CreatedListItem> {
  const fields: Record<string, unknown> = {};
  if (input.Title) fields.Title = input.Title;
  if (input.Yourname) fields.Yourname = input.Yourname;
  if (input.Yourteam_x002f_function)
    fields.Yourteam_x002f_function = input.Yourteam_x002f_function;
  if (input.Region_x002f_Division)
    fields.Region_x002f_Division = input.Region_x002f_Division;
  if (input.Whatproblemareyoutryingtosolve)
    fields.Whatproblemareyoutryingtosolve = input.Whatproblemareyoutryingtosolve;
  if (input.Howoftendoesthisproblemoccur_x00)
    fields.Howoftendoesthisproblemoccur_x00 =
      input.Howoftendoesthisproblemoccur_x00;
  if (input.Whoisimpactedbythisproblemtoday_)
    fields.Whoisimpactedbythisproblemtoday_ =
      input.Whoisimpactedbythisproblemtoday_;
  if (input.Ifthisproblemweresolved_x002c_wh)
    fields.Ifthisproblemweresolved_x002c_wh =
      input.Ifthisproblemweresolved_x002c_wh;
  if (input.Pleaseexplaintheanticipatedimpac)
    fields.Pleaseexplaintheanticipatedimpac =
      input.Pleaseexplaintheanticipatedimpac;
  if (
    input.Whattypeofimpacttodoyouexpect_x0 &&
    input.Whattypeofimpacttodoyouexpect_x0.length
  )
    fields.Whattypeofimpacttodoyouexpect_x0 =
      input.Whattypeofimpacttodoyouexpect_x0;
  if (input.Priority != null) fields.Priority = input.Priority;
  // Write to the list's real internal column names (verified against the
  // AISupportBot site's field settings). The previous display-name keys
  // ("Proposed solution", etc.) were silently dropped by Graph.
  if (input.ProposedSolution)
    fields.Ifyes_x002c_brieflydescribethepr = input.ProposedSolution;
  if (input.ExistingExamples)
    fields.Arethereexistingexampleswecouldm = input.ExistingExamples;
  if (input.DataInputs)
    fields.Whatdataorinputswouldbeinvolved_ = input.DataInputs;
  if (input.ConstraintsRisks)
    fields.Arethereanyconstraints_x002c_ris = input.ConstraintsRisks;
  if (input.AnythingElse)
    fields.Anythingelseweshouldknow_x003f_ = input.AnythingElse;
  if (input.Sponsor)
    fields.Isthereasponsorforthisinitiative = input.Sponsor;
  if (input.Notes) fields.Notes = input.Notes;
  // "New" is the list's actual default status — "Pending" is not a valid
  // choice on the Initiative list and was rejected by SharePoint.
  fields.Status = input.Status || "New";
  const created = await createListItem(token, INITIATIVE_LIST_ID, fields);
  void logActivity("Initiative", "Created", `Submitted "${input.Title}"`, "initiative", created.id);
  return created;
}

export async function updateInitiativeItem(
  token: string,
  itemId: string,
  input: Partial<NewInitiativeFields>
): Promise<void> {
  const fields: Record<string, unknown> = {};
  if (input.Title) fields.Title = input.Title;
  if (input.Yourname) fields.Yourname = input.Yourname;
  if (input.Yourteam_x002f_function)
    fields.Yourteam_x002f_function = input.Yourteam_x002f_function;
  // Closed choice columns — skip empty strings or SharePoint returns 400.
  if (input.Region_x002f_Division)
    fields.Region_x002f_Division = input.Region_x002f_Division;
  if (input.Whatproblemareyoutryingtosolve)
    fields.Whatproblemareyoutryingtosolve =
      input.Whatproblemareyoutryingtosolve;
  if (input.Howoftendoesthisproblemoccur_x00)
    fields.Howoftendoesthisproblemoccur_x00 =
      input.Howoftendoesthisproblemoccur_x00;
  if (input.Whoisimpactedbythisproblemtoday_)
    fields.Whoisimpactedbythisproblemtoday_ =
      input.Whoisimpactedbythisproblemtoday_;
  if (input.Ifthisproblemweresolved_x002c_wh)
    fields.Ifthisproblemweresolved_x002c_wh =
      input.Ifthisproblemweresolved_x002c_wh;
  if (input.Pleaseexplaintheanticipatedimpac)
    fields.Pleaseexplaintheanticipatedimpac =
      input.Pleaseexplaintheanticipatedimpac;
  if (input.Whattypeofimpacttodoyouexpect_x0 && input.Whattypeofimpacttodoyouexpect_x0.length)
    fields.Whattypeofimpacttodoyouexpect_x0 =
      input.Whattypeofimpacttodoyouexpect_x0;
  if (input.Priority != null) fields.Priority = input.Priority;
  if (input.ProposedSolution)
    fields.Ifyes_x002c_brieflydescribethepr = input.ProposedSolution;
  if (input.ExistingExamples)
    fields.Arethereexistingexampleswecouldm = input.ExistingExamples;
  if (input.DataInputs)
    fields.Whatdataorinputswouldbeinvolved_ = input.DataInputs;
  if (input.ConstraintsRisks)
    fields.Arethereanyconstraints_x002c_ris = input.ConstraintsRisks;
  if (input.AnythingElse)
    fields.Anythingelseweshouldknow_x003f_ = input.AnythingElse;
  if (input.Sponsor)
    fields.Isthereasponsorforthisinitiative = input.Sponsor;
  if (input.Notes) fields.Notes = input.Notes;
  if (input.Status) fields.Status = input.Status;
  await updateListItem(token, INITIATIVE_LIST_ID, itemId, fields);
  // A bare {Status} payload (e.g. the "In progress" board's move arrows)
  // gets its own event name rather than the generic "Updated" — distinct
  // and more informative in the activity log.
  const label = input.Title ?? itemId;
  if (input.Status && Object.keys(fields).length === 1) {
    void logActivity(
      "Initiative",
      "StatusChanged",
      `Moved "${label}" to "${input.Status}"`,
      "initiative",
      itemId
    );
  } else {
    void logActivity("Initiative", "Updated", `Updated "${label}"`, "initiative", itemId);
  }
}

/** Initiative columns that hold the two denormalized review scores.
 *  Internal column names per the list's field settings. */
export type InitiativeScoreField =
  | "BusinessValueScore"
  | "TechnicalFeasibilityScore";

/** Write a single review composite back onto the Initiative list row, so
 *  the catalog can show the latest Business / Technical score without
 *  loading the review. PATCH only touches the one score column. */
export async function updateInitiativeScore(
  token: string,
  itemId: string,
  field: InitiativeScoreField,
  score: number
): Promise<void> {
  await updateListItem(token, INITIATIVE_LIST_ID, itemId, { [field]: score });
}

/** Advance the Initiative's workflow Status as a review stage completes
 *  (Business done → "Under Technical Review", Technical done → "In
 *  Backlog" — see saveReview() in reviews.ts for the actual logic).
 *  PATCH only touches the Status column. */
export async function updateInitiativeStatus(
  token: string,
  itemId: string,
  status: string
): Promise<void> {
  await updateListItem(token, INITIATIVE_LIST_ID, itemId, { Status: status });
  void logActivity(
    "Initiative",
    "StatusChanged",
    `Initiative ${itemId} advanced to "${status}" after a review`,
    "initiative",
    itemId
  );
}

// ---------- Per-kind metadata ----------
//
// Single source of truth for: which SharePoint list backs a kind, how to
// map raw rows to typed items, and which field name holds the "status"
// column on that list. Admin actions (approve) use the status-field name
// to drive a one-call PATCH.

export interface ListKindMeta {
  listId: string;
  mapper: (raw: GraphListItem) => CatalogItem;
  statusField: string;
}

export function listKindMeta(kind: ListKind): ListKindMeta {
  if (kind === "library") {
    return {
      listId: LIBRARY_LIST_ID,
      mapper: mapLibrary,
      statusField: "AssetStatus",
    };
  }
  if (kind === "initiative") {
    return {
      listId: INITIATIVE_LIST_ID,
      mapper: mapInitiative,
      statusField: "Status",
    };
  }
  return {
    listId: PLATFORM_LIST_ID,
    mapper: mapPlatform,
    statusField: "Status",
  };
}

/**
 * Per-kind status vocabulary. Each list uses a different word for the
 * "live in the catalog" state — Library = Published, Initiative = Live,
 * Platform = Approved — so anything that filters, labels, or writes the
 * status must read from here rather than hardcoding a single value.
 *
 * `choices` is what the admin edit modal suggests in its datalist; the
 * input is free-text, so admins can still type something else if needed.
 */
export interface ListStatusConfig {
  /** SharePoint column name that holds the status for this list. */
  field: string;
  /** Value that means "this item is live to end users" on this list. */
  approvedValue: string;
  /** Suggested status values for the admin edit modal. */
  choices: string[];
}

export function listStatusConfig(kind: ListKind): ListStatusConfig {
  if (kind === "library") {
    return {
      field: "AssetStatus",
      approvedValue: "Published",
      // Real choices from the Library list's Asset Status column. "Pending"
      // is NOT a valid choice here — new submissions default to "Under Review".
      choices: ["Under Review", "Published", "Archived"],
    };
  }
  if (kind === "initiative") {
    return {
      field: "Status",
      approvedValue: "Live",
      // Real choice set from the Initiative list's Status column.
      choices: [
        "New",
        "Under Council Review",
        "Under Technical Review",
        "Under Cyber Security Review",
        "Approved",
        "Rejected",
        "Info Required",
        "In Backlog",
        "Design",
        "Development",
        "Testing",
        "Live",
        "Cancelled",
      ],
    };
  }
  return {
    field: "Status",
    approvedValue: "Approved",
    // Real choice set from the Platform list's Status column.
    choices: [
      "New",
      "Under IT Review",
      "Under Cyber Security Review",
      "Under Legal Review",
      "Approved",
      "Forbidden",
    ],
  };
}

/** One-shot admin helper to flip an item to the per-kind "approved" value
 *  without round-tripping through the full edit modal. Defaults pull from
 *  `listStatusConfig(kind)` so Library items become "Published", Initiatives
 *  become "Live", and Platforms become "Approved". */
/** "library" -> "Library", matching the Title-case category names the
 *  activity log uses everywhere else. */
function kindToCategory(kind: ListKind): ActivityCategory {
  return (kind.charAt(0).toUpperCase() + kind.slice(1)) as ActivityCategory;
}

export async function approveItem(
  token: string,
  kind: ListKind,
  itemId: string,
  approvedValue?: string
): Promise<void> {
  const meta = listKindMeta(kind);
  const value = approvedValue ?? listStatusConfig(kind).approvedValue;
  await updateListItem(token, meta.listId, itemId, {
    [meta.statusField]: value,
  });
  void logActivity(
    kindToCategory(kind),
    "Approved",
    `Marked item ${itemId} as "${value}"`,
    kind,
    itemId
  );
}

export async function deleteItem(
  token: string,
  kind: ListKind,
  itemId: string
): Promise<void> {
  const meta = listKindMeta(kind);
  await deleteListItem(token, meta.listId, itemId);
  void logActivity(kindToCategory(kind), "Deleted", `Deleted item ${itemId}`, kind, itemId);
}

// ---------- Legacy bulk fetch (kept for backward compatibility) ----------

async function fetchAllItems(
  token: string,
  listId: string
): Promise<GraphListItem[]> {
  const out: GraphListItem[] = [];
  let next:
    | string
    | undefined = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items?expand=fields&$top=100`;

  while (next) {
    const res = await fetch(next, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Graph API ${res.status}: ${res.statusText}${
          text ? ` — ${text.slice(0, 240)}` : ""
        }`
      );
    }
    const data = (await res.json()) as GraphListResponse;
    out.push(...data.value);
    next = data["@odata.nextLink"];
  }
  return out;
}

export interface CatalogData {
  library: LibraryItem[];
  initiatives: InitiativeItem[];
  platforms: PlatformItem[];
  all: CatalogItem[];
}

export async function fetchCatalog(
  getToken: () => Promise<string>
): Promise<CatalogData> {
  const token = await getToken();

  const [libRaw, initRaw, platRaw] = await Promise.all([
    fetchAllItems(token, LIBRARY_LIST_ID).catch((e) => {
      console.error("Library fetch failed:", e);
      return [];
    }),
    fetchAllItems(token, INITIATIVE_LIST_ID).catch((e) => {
      console.error("Initiatives fetch failed:", e);
      return [];
    }),
    fetchAllItems(token, PLATFORM_LIST_ID).catch((e) => {
      console.error("Platforms fetch failed:", e);
      return [];
    }),
  ]);

  const library = libRaw
    .map(mapLibrary)
    .sort((a, b) => a.title.localeCompare(b.title));
  const initiatives = initRaw
    .map(mapInitiative)
    .sort((a, b) => a.title.localeCompare(b.title));
  const platforms = platRaw
    .map(mapPlatform)
    .sort((a, b) => a.title.localeCompare(b.title));

  return {
    library,
    initiatives,
    platforms,
    all: [...library, ...initiatives, ...platforms],
  };
}

// ---------- Counts (used by Dashboard / Insights) ----------

export async function fetchListCount(
  token: string,
  listId: string,
  filter?: string
): Promise<number> {
  // $count is not supported on /items directly; the cheap path is to fetch
  // only the id field with a small page and walk @odata.nextLink, BUT for
  // dashboard tiles we typically want a fast approximate count. Use the
  // smallest projection and follow nextLink up to a cap.
  const u = new URL(`${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items`);
  u.searchParams.set("$select", "id");
  u.searchParams.set("$top", "100");
  if (filter) u.searchParams.set("$filter", filter);

  let next: string | undefined = u.toString();
  let total = 0;
  let pages = 0;
  while (next && pages < 30) {
    const res = await fetch(next, {
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
      },
    });
    if (!res.ok) {
      throw new Error(`Graph count ${res.status}: ${res.statusText}`);
    }
    const data = (await res.json()) as GraphListResponse;
    total += data.value.length;
    next = data["@odata.nextLink"];
    pages += 1;
  }
  return total;
}

// ---------- Recent items across all 3 lists ----------

export interface RecentItem {
  kind: ListKind;
  item: CatalogItem;
}

async function fetchRecentForList(
  token: string,
  listId: string,
  top: number,
  mapper: (raw: GraphListItem) => CatalogItem,
  kind: ListKind
): Promise<RecentItem[]> {
  const u = new URL(`${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items`);
  u.searchParams.set("expand", "fields");
  u.searchParams.set("$top", String(top));
  u.searchParams.set("$orderby", "lastModifiedDateTime desc");
  const res = await fetch(u.toString(), {
    cache: "no-store",
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
    },
  });
  if (!res.ok) throw new Error(`Graph recent ${res.status}`);
  const data = (await res.json()) as GraphListResponse;
  return data.value.map((raw) => ({ kind, item: mapper(raw) }));
}

export async function fetchRecentAcrossLists(
  token: string,
  perList = 4
): Promise<RecentItem[]> {
  const [lib, init, plat] = await Promise.all([
    fetchRecentForList(token, LIBRARY_LIST_ID, perList, mapLibrary, "library").catch(
      () => []
    ),
    fetchRecentForList(
      token,
      INITIATIVE_LIST_ID,
      perList,
      mapInitiative,
      "initiative"
    ).catch(() => []),
    fetchRecentForList(
      token,
      PLATFORM_LIST_ID,
      perList,
      mapPlatform,
      "platform"
    ).catch(() => []),
  ]);
  const all = [...lib, ...init, ...plat];
  return all
    .sort((a, b) => {
      const ad = a.item.modified || a.item.created || "";
      const bd = b.item.modified || b.item.created || "";
      return bd.localeCompare(ad);
    })
    .slice(0, perList * 2);
}

// ---------- AI News list ----------
//
// The list is structured as ONE row per period (e.g. "May 2026") with five
// long-text columns — one per dashboard section. We only ever surface the
// single latest record (top 1 by Created desc).
//
// SharePoint flattens column display names to internal names by stripping
// spaces, so "Executive Summary" -> ExecutiveSummary, "Microsoft AI" ->
// MicrosoftAI, etc.

function pickField(f: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = str(f[k]);
    if (v) return v;
  }
  return "";
}

function mapDigest(raw: GraphListItem): AINewsDigest {
  const f = (raw.fields ?? {}) as Record<string, unknown>;
  return {
    id: raw.id,
    title: str(f.Title) || str(f.LinkTitle) || "Latest digest",
    executiveSummary: pickField(
      f,
      "ExecutiveSummary",
      "Executive_x0020_Summary",
      "Executive_Summary"
    ),
    microsoftAI: pickField(
      f,
      "MicrosoftAI",
      "Microsoft_x0020_AI",
      "Microsoft_AI"
    ),
    frontierModels: pickField(
      f,
      "FrontierModels",
      "Frontier_x0020_Models",
      "Frontier_Models"
    ),
    aiHardware: pickField(
      f,
      "AIHardware",
      "AI_x0020_Hardware",
      "AI_Hardware"
    ),
    toolsAndApps: pickField(
      f,
      "ToolsandApps",
      "ToolsAndApps",
      "Tools_x0020_and_x0020_Apps",
      "Tools_and_Apps"
    ),
    modified: raw.lastModifiedDateTime,
    created: raw.createdDateTime,
    webUrl: raw.webUrl,
    raw: f,
  };
}

export async function fetchLatestAINewsDigest(
  token: string
): Promise<AINewsDigest | null> {
  const u = new URL(
    `${GRAPH_BASE}/sites/${SITE_ID}/lists/${NEWS_LIST_ID}/items`
  );
  u.searchParams.set("expand", "fields");
  u.searchParams.set("$top", "1");
  u.searchParams.set("$orderby", "createdDateTime desc");
  const res = await fetch(u.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
    },
  });
  if (!res.ok) throw new Error(`Graph news ${res.status}`);
  const data = (await res.json()) as GraphListResponse;
  if (!data.value.length) return null;
  return mapDigest(data.value[0]);
}

// ---------- Training list ----------
//
// One row per session. Hyperlink columns (JoinUrl, RecordingUrl, MaterialsUrl)
// come back as objects: { Url, Description }. Tags is a multi-choice column
// and arrives as an array of strings. Host is a single-line text field —
// kept as a display name string here, not a Person column.

function pickArray(v: unknown): string[] {
  if (!v) return [];
  if (Array.isArray(v))
    return v.map((x) => (typeof x === "string" ? x : String(x))).filter(Boolean);
  if (typeof v === "string") return v ? [v] : [];
  return [];
}

function pickNumber(v: unknown): number | undefined {
  if (v == null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeTimezone(v: string): string {
  if (!v) return "Eastern Time";
  if (/time$/i.test(v.trim())) return v.trim();
  return `${v.trim()} Time`;
}

function normalizeFormat(v: string): TrainingFormat {
  const t = v.trim().toLowerCase();
  if (t === "in-person" || t === "in person") return "in-person";
  if (t === "hybrid") return "hybrid";
  return "virtual";
}

export function mapTraining(raw: GraphListItem): TrainingSession {
  const f = (raw.fields ?? {}) as Record<string, unknown>;
  const join = pickUrl(f.JoinUrl);
  const recording = pickUrl(f.RecordingUrl);
  const materials = pickUrl(f.MaterialsUrl);
  return {
    id: raw.id,
    title: str(f.Title) || str(f.LinkTitle) || "Untitled session",
    sessionLabel: str(f.SessionLabel) || undefined,
    description: str(f.Description),
    start: str(f.StartDateTime),
    end: str(f.EndDateTime),
    timezone: normalizeTimezone(str(f.TimeZone)),
    format: normalizeFormat(str(f.Format)),
    location: str(f.Location) || undefined,
    host: str(f.Host),
    attendees: [],
    capacity: pickNumber(f.Capacity),
    joinUrl: join.url || undefined,
    recordingUrl: recording.url || undefined,
    materialsUrl: materials.url || undefined,
    tags: pickArray(f.Tags),
    status: str(f.Status) || undefined,
    webUrl: raw.webUrl,
    modified: raw.lastModifiedDateTime,
    created: raw.createdDateTime,
    raw: f,
  };
}

export async function fetchTrainingSessions(
  token: string
): Promise<TrainingSession[]> {
  const out: TrainingSession[] = [];
  let next: string | undefined = (() => {
    const u = new URL(
      `${GRAPH_BASE}/sites/${SITE_ID}/lists/${TRAINING_LIST_ID}/items`
    );
    u.searchParams.set("expand", "fields,createdBy");
    u.searchParams.set("$top", "100");
    u.searchParams.set("$orderby", "fields/StartDateTime asc");
    return u.toString();
  })();

  while (next) {
    const res = await fetch(next, {
      headers: {
        Authorization: `Bearer ${token}`,
        Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Graph training ${res.status}: ${res.statusText}${
          text ? ` — ${text.slice(0, 240)}` : ""
        }`
      );
    }
    const data = (await res.json()) as GraphListResponse;
    for (const raw of data.value) {
      const session = mapTraining(raw);
      if (session.status === "Cancelled") continue;
      if (!session.start || !session.end) continue;
      out.push(session);
    }
    next = data["@odata.nextLink"];
  }
  return out;
}

/** Fetches every item on a list (walking @odata.nextLink to completion) — for
 *  counting/aggregation use cases where pagination in the UI doesn't apply. */
export async function fetchAllListItems<T extends CatalogItem>(
  token: string,
  listId: string,
  mapper: (raw: GraphListItem) => T
): Promise<T[]> {
  const out: T[] = [];
  let next: string | undefined = (() => {
    const u = new URL(`${GRAPH_BASE}/sites/${SITE_ID}/lists/${listId}/items`);
    u.searchParams.set("expand", "fields,createdBy");
    u.searchParams.set("$top", "200");
    return u.toString();
  })();
  while (next) {
    const res = await fetch(next, {
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Graph list ${res.status}: ${res.statusText}${text ? ` — ${text.slice(0, 240)}` : ""}`
      );
    }
    const data = (await res.json()) as GraphListResponse;
    out.push(...data.value.map(mapper));
    next = data["@odata.nextLink"];
  }
  return out;
}

export interface GraphUserLite {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
  jobTitle: string | null;
}

// Brookfield's privileged/admin accounts share the same display name as the
// person's real account, distinguished only by an "xadm_"-prefixed UPN/mail
// local-part (e.g. xadm_bdeschambault@... alongside brennan.deschambault@...).
// When a display-name search turns up both, we want the real one.
function looksLikeAdminAccount(value: string | null | undefined): boolean {
  if (!value) return false;
  const local = value.split("@")[0].toLowerCase();
  return local.startsWith("xadm_") || local.startsWith("xadm-");
}

/** Best-effort resolution of a Champion's AAD identity from whatever text we
 *  extracted from the Person field — an email is used directly; a display
 *  name is resolved via Graph's fuzzy user search. When multiple accounts
 *  share that display name (e.g. a real account + an "xadm_" admin account
 *  for the same person), the non-admin account is preferred. */
export async function resolveUserByNameOrEmail(
  token: string,
  nameOrEmail: string
): Promise<GraphUserLite | null> {
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return null;
  const select = "id,displayName,mail,userPrincipalName,jobTitle";
  const headers = { Authorization: `Bearer ${token}` };

  if (trimmed.includes("@")) {
    // Fast path — only works if this value is the user's exact sign-in UPN.
    const direct = await fetch(
      `${GRAPH_BASE}/users/${encodeURIComponent(trimmed)}?$select=${select}`,
      { cache: "no-store", headers }
    );
    if (direct.ok) return (await direct.json()) as GraphUserLite;
    console.log(
      `[resolveUserByNameOrEmail] direct UPN lookup failed (${direct.status}) for "${trimmed}" — falling back to a mail/alias search`
    );

    // Fallback — the value may be a secondary "mail" address or SMTP alias
    // rather than the sign-in UPN (common when they differ in a tenant), so
    // search by mail/proxyAddresses instead of assuming it's the UPN.
    const esc = trimmed.replace(/'/g, "''");
    const u = new URL(`${GRAPH_BASE}/users`);
    u.searchParams.set(
      "$filter",
      `mail eq '${esc}' or proxyAddresses/any(p:p eq 'smtp:${esc}')`
    );
    u.searchParams.set("$select", select);
    u.searchParams.set("$top", "1");
    u.searchParams.set("$count", "true");
    const res = await fetch(u.toString(), {
      cache: "no-store",
      headers: { ...headers, ConsistencyLevel: "eventual" },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      console.error(
        `[resolveUserByNameOrEmail] mail/alias search failed (${res.status}) for "${trimmed}":`,
        text.slice(0, 300)
      );
      return null;
    }
    const data = (await res.json()) as { value: GraphUserLite[] };
    if (!data.value[0]) {
      console.log(`[resolveUserByNameOrEmail] no user matched "${trimmed}" by mail or alias either`);
    }
    return data.value[0] ?? null;
  }

  const u = new URL(`${GRAPH_BASE}/users`);
  u.searchParams.set("$search", `"displayName:${trimmed}"`);
  u.searchParams.set("$select", select);
  u.searchParams.set("$top", "10");
  const res = await fetch(u.toString(), {
    cache: "no-store",
    headers: { ...headers, ConsistencyLevel: "eventual" },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error(
      `[resolveUserByNameOrEmail] displayName search failed (${res.status}) for "${trimmed}":`,
      text.slice(0, 300)
    );
    return null;
  }
  const data = (await res.json()) as { value: GraphUserLite[] };
  if (data.value.length > 1) {
    console.log(
      `[resolveUserByNameOrEmail] "${trimmed}" matched ${data.value.length} accounts:`,
      data.value.map((u) => u.userPrincipalName || u.mail).join(", ")
    );
  }
  const nonAdmin = data.value.find(
    (u) => !looksLikeAdminAccount(u.userPrincipalName) && !looksLikeAdminAccount(u.mail)
  );
  return nonAdmin ?? data.value[0] ?? null;
}
